import { useParams } from "react-router";

/**
 * The token from an emailed link.
 *
 * Links are minted as `/signin/<token>` — a path segment rather than a query
 * parameter.
 *
 * **Not a security boundary**, and it should not be mistaken for one: a path is
 * exactly as visible as a query string in the URL bar, in browser history and in
 * a server access log, and anything able to fetch one is able to fetch the
 * other. What protects these links is that they are 256 bits of CSPRNG output,
 * single use, short lived, and spent by a POST rather than by being opened.
 *
 * What it does buy is two smaller things. `?token=` **says what the string is**
 * to anyone reading the URL over a shoulder or in a log, where a path segment is
 * just an opaque path. And `location.search` is what analytics scripts, error
 * reporters and session recorders capture by habit, so a token that is never in
 * it cannot be swept up by a tool somebody adds later without thinking about it.
 *
 * There is deliberately **no `?token=` fallback**. The format changed while
 * nothing was in anybody's inbox, so a compatibility path would have been dead
 * code from the day it was written — and a dead branch in the one place a
 * credential is read is worse than no branch.
 */
export function useEmailToken(): string {
  const params = useParams<{ token?: string }>();
  return params.token ?? "";
}
